import React, { useEffect, useState, useCallback } from "react";
import { Sparkles, Plus, Trash2, RefreshCw, Save, Eye, EyeOff, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const emptyArticle = {
  id: null,
  title: "",
  excerpt: "",
  cover_image: "",
  content_markdown: "",
  published: false,
};

export default function BlogAdminTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiTitle, setAiTitle] = useState("");
  const [aiAngle, setAiAngle] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/articles");
      setItems(r.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get("/admin/articles");
        if (active) setItems(r.data.items || []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const openArticle = async (id) => {
    if (!id) {
      setEditing({ ...emptyArticle });
      return;
    }
    try {
      const r = await api.get(`/admin/articles/${id}`);
      setEditing(r.data);
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  const save = async () => {
    if (!editing?.title?.trim()) {
      alert("Il titolo è obbligatorio");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/articles/${editing.id}`, {
          title: editing.title,
          excerpt: editing.excerpt,
          cover_image: editing.cover_image,
          content_markdown: editing.content_markdown,
          published: editing.published,
        });
      } else {
        await api.post("/admin/articles", {
          title: editing.title,
          excerpt: editing.excerpt,
          cover_image: editing.cover_image,
          content_markdown: editing.content_markdown,
          published: editing.published,
        });
      }
      setEditing(null);
      load();
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      load();
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  const togglePublish = async (a) => {
    try {
      await api.put(`/admin/articles/${a.id}`, { published: !a.published });
      load();
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  const generateAiDraft = async () => {
    if (!aiTitle.trim()) {
      alert("Inserisci un titolo per la bozza");
      return;
    }
    setAiLoading(true);
    try {
      const r = await api.post(
        "/admin/articles/ai-draft",
        { title: aiTitle.trim(), angle: aiAngle.trim() },
        { timeout: 120000 },
      );
      setEditing({
        ...emptyArticle,
        title: r.data.title || aiTitle,
        excerpt: r.data.excerpt || "",
        content_markdown: r.data.content_markdown || "",
      });
      setAiTitle("");
      setAiAngle("");
    } catch (e) {
      alert("Errore AI: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAiLoading(false);
    }
  };

  // ---------- Edit view ----------
  if (editing) {
    return (
      <div data-testid="blog-edit">
        <button
          onClick={() => setEditing(null)}
          className="text-sm text-zinc-400 hover:text-white mb-5"
        >
          ← Torna alla lista
        </button>

        <h1 className="font-display font-black text-2xl text-white mb-5">
          {editing.id ? "Modifica articolo" : "Nuovo articolo"}
        </h1>

        <div className="space-y-5">
          <div>
            <label className="block text-xs font-mono-bdd uppercase tracking-wider text-zinc-500 mb-2">
              Titolo
            </label>
            <input
              data-testid="article-title"
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
              placeholder="Migliori set LEGO Star Wars del 2026"
            />
          </div>
          <div>
            <label className="block text-xs font-mono-bdd uppercase tracking-wider text-zinc-500 mb-2">
              Meta description (SEO) — 150-160 caratteri
            </label>
            <input
              data-testid="article-excerpt"
              value={editing.excerpt}
              onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
              maxLength={300}
            />
            <div className="text-xs text-zinc-500 mt-1">
              {editing.excerpt?.length || 0} / 160 ottimali
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono-bdd uppercase tracking-wider text-zinc-500 mb-2">
              Cover image URL (opzionale)
            </label>
            <input
              data-testid="article-cover"
              value={editing.cover_image}
              onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="block text-xs font-mono-bdd uppercase tracking-wider text-zinc-500 mb-2">
              Contenuto (Markdown)
            </label>
            <textarea
              data-testid="article-content"
              value={editing.content_markdown}
              onChange={(e) =>
                setEditing({ ...editing, content_markdown: e.target.value })
              }
              rows={24}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60 font-mono text-sm leading-relaxed resize-y"
            />
            <div className="text-xs text-zinc-500 mt-1">
              Supporta Markdown: ## titoli, **grassetto**, [link](url), - liste
            </div>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer select-none">
            <input
              data-testid="article-published"
              type="checkbox"
              checked={editing.published}
              onChange={(e) =>
                setEditing({ ...editing, published: e.target.checked })
              }
              className="w-5 h-5 accent-[#FBD51C]"
            />
            <span className="text-sm text-zinc-300">
              {editing.published ? "Pubblicato (visibile sul sito)" : "Bozza"}
            </span>
          </label>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={save}
              disabled={saving}
              data-testid="article-save"
              className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] text-black font-black uppercase tracking-wider text-xs px-5 py-2.5 rounded-lg disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Salvataggio..." : "Salva"}
            </button>
            <button
              onClick={() => setEditing(null)}
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800 text-sm font-bold px-5 py-2.5 rounded-lg"
            >
              Annulla
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- List view ----------
  return (
    <div data-testid="blog-tab">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-black text-2xl text-white">Blog</h1>
          <p className="text-zinc-500 text-sm">
            Guide e news per il SEO. Crea bozze con AI, edita e pubblica.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800 text-sm font-bold px-4 py-2 rounded-lg"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
          </button>
          <button
            onClick={() => openArticle(null)}
            data-testid="article-new"
            className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] text-black font-black uppercase tracking-wider text-xs px-4 py-2 rounded-lg"
          >
            <Plus size={14} /> Nuovo articolo
          </button>
        </div>
      </div>

      {/* AI assist box */}
      <div className="bg-gradient-to-br from-[#FBD51C]/10 to-zinc-900 border border-[#FBD51C]/30 rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-[#FBD51C]" />
          <h2 className="font-display text-lg text-white">Crea bozza con AI</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Genera una bozza completa di articolo (~1000 parole) in ~30 secondi.
          Poi puoi modificarla e personalizzarla prima di pubblicare.
        </p>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <input
            data-testid="ai-title"
            value={aiTitle}
            onChange={(e) => setAiTitle(e.target.value)}
            placeholder="Titolo es: Migliori set LEGO Technic 2026"
            className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
          />
          <input
            data-testid="ai-angle"
            value={aiAngle}
            onChange={(e) => setAiAngle(e.target.value)}
            placeholder="Focus opzionale es: per principianti, budget €50-150..."
            className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
          />
        </div>
        <button
          onClick={generateAiDraft}
          disabled={aiLoading || !aiTitle.trim()}
          data-testid="ai-generate"
          className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] text-black font-black uppercase tracking-wider text-xs px-5 py-2.5 rounded-lg disabled:opacity-60"
        >
          {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {aiLoading ? "Generazione in corso (~30s)..." : "Genera bozza"}
        </button>
      </div>

      {/* Articles list */}
      {!loading && items.length === 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-10 text-center">
          <div className="text-zinc-400">Nessun articolo creato</div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((a) => (
          <div
            key={a.id}
            data-testid={`article-row-${a.slug}`}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-4 flex-wrap"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`text-[10px] font-mono-bdd uppercase tracking-wider px-2 py-0.5 rounded ${
                    a.published
                      ? "bg-emerald-950/60 text-emerald-300 border border-emerald-900"
                      : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                  }`}
                >
                  {a.published ? "Pubblicato" : "Bozza"}
                </span>
                <span className="text-[10px] text-zinc-500">/{a.slug}</span>
              </div>
              <div className="font-bold text-white line-clamp-1">{a.title}</div>
              {a.excerpt && (
                <div className="text-xs text-zinc-500 line-clamp-1 mt-1">
                  {a.excerpt}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => togglePublish(a)}
                title={a.published ? "Nascondi" : "Pubblica"}
                className="text-zinc-400 hover:text-[#FBD51C] p-2"
              >
                {a.published ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button
                onClick={() => openArticle(a.id)}
                className="text-sm font-bold text-[#FBD51C] hover:underline px-3"
              >
                Modifica
              </button>
              <button
                onClick={() => remove(a.id)}
                className="text-zinc-400 hover:text-rose-400 p-2"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
